<template>
  <div class="discount-campaigns">
    <teleport to="body">
      <overlay
        key="discountCampaignsActivatedModal"
        block-class="discount-campaigns-overlay"
        namespace="discountCampaignsActivatedModal"
        direction="up"
        :show-close="false"
        type="modal"
      >
        <template #body>
          <div class="discount-campaigns__header">
            <button
              class="discount-campaigns__close icon-button"
              type="button"
              @click="closeOverlay('discountCampaignsActivatedModal')"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />

              <icon-close />
            </button>

            <p
              class="discount-campaigns__title text-mobile-headlines-h4 text-desktop-headlines-h4"
              v-text="notificationTitle"
            />

            <span
              class="discount-campaigns__code text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
              v-text="notificationCode"
            />
          </div>

          <div
            class="discount-campaigns__content"
            js-data-notification="overlaytext"
          >
            <p
              class="text-center text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
              v-html="notificationText"
            />
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Discount campaigns (discount-campaigns)
 * -----------------------------------------------------------------------------
 * Global component used to query and apply discount campaigns.
 * - JS Cookie import is found otherwise cookie functionality wouldn't work
 *
 */
// eslint-disable-next-line import/no-unresolved
import Cookies from 'js-cookie';
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useIndexStore from '~stores/index';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import Overlay from '~global/overlay/overlay.vue';
import IconClose from '~icons/directional-navigation/close.svg';
import { dateTime, isCurrentMarket } from '~/helpers/general';
import {
  getCampaignCode,
  getTypes,
  isOrderCode,
  validCampaignCodes,
  validInfluencerCodes,
  validPrivateCodes,
} from '~/helpers/discounts';
import { retryWithBackoff } from '~/helpers/retry';

export default {
  name: 'DiscountCampaigns',

  components: {
    IconClose,
    Overlay,
  },

  data() {
    return {
      campaigns: [],
      cnvsGlobal: true,
      influencers: [],
      privateCampaigns: [],
      campaignCodes: [],
      localActiveCampaign: false,
      localCode: '',
      pendingCodeResolution: null,
      locationHost: location?.host?.replace('www.', ''),
      newNotificationCode: '',
      notificationText: '',
      urlApplicable: true,
    };
  },

  computed: {
    ...mapState(useIndexStore, { hydrated: 'hydrated' }),
    ...mapState(useCartStore, {
      cart: 'response',
      removeOrderCode: 'removeOrderCode',
    }),
    ...mapState(useDiscountCampaignsStore, {
      activeCampaign: 'active',
      removeCode: 'removeCode',
      enteredCode: 'enteredCode',
      urlDiscount: 'urlDiscount',
      validNativeCode: 'validNativeCode',
      initialCheck: 'initialCheck',
    }),
    ...mapState(useOverlaysStore, {
      activeOverlay: 'active',
    }),

    /**
     * Compute promoted campaign based.
     * - Promoted campaign must be 'discount' type, have valid dates, and if it
     *   has markets then the current market must match.
     * - Returns nothing when active campaign is set.
     * @returns {Boolean|Object}
     */
    localPromotedCampaign() {
      if (this.localActiveCampaign || this.activeCampaign) {
        return false;
      }

      return this.campaigns?.find((campaign) => {
        if (campaign.type !== 'discount' || !campaign.promote) {
          return false;
        }

        return (
          this.getValidCampaignDate(campaign) &&
          this.getValidCampaignMarket(campaign)
        );
      });
    },

    notificationCode() {
      /**
       * `newNotificationCode` is the code captured by the in-flight
       * resolution that's actually showing this modal (see
       * `checkNotificationPopup()`). It must win over `enteredCode`/
       * `localCode` - those are shared, live state that a different,
       * concurrently-resolving code entry can overwrite while this modal
       * is still open, which would otherwise silently swap the displayed
       * code for the wrong one.
       */
      return this.newNotificationCode || this.enteredCode || this.localCode;
    },

    notificationTitle() {
      if (this.urlApplicable) {
        return this.$string('discount_campaigns.activated_modal.title');
      }

      return this.$string('discount_campaigns.activated_modal.code');
    },
  },

  watch: {
    removeOrderCode() {
      if (!this.removeOrderCode) {
        return;
      }

      const orderCode = this.cart.discount_codes.filter((discount) => {
        return isOrderCode(discount.code);
      });

      if (this.cart.items.some((item) => item.product_type === 'book')) {
        this.clearCode('invalid', orderCode[0]?.code, 'book');
      } else if (
        this.cart.items.some((item) => item.product_type === 'deposit')
      ) {
        this.clearCode('invalid', orderCode[0]?.code, 'deposit');
      } else {
        this.clearCode('invalid', orderCode[0]?.code, 'order');
      }

      this.setEnteredCode('');
      this.setRemoveOrderCode(false);
    },

    removeCode() {
      if (!this.removeCode.code) {
        return;
      }

      this.clearCode(this.removeCode.state, this.removeCode.code);
    },

    enteredCode() {
      if (!this.enteredCode) {
        return;
      }

      // Track the in-flight resolution so `hydrated()` can await it before
      // trusting `this.activeCampaign` (see comment there).
      this.pendingCodeResolution = this.determineValidCode();
    },

    /**
     * Watch local active campaign to update Pinia state.
     * - Don't immediately execute as mounted functionality needs to check if
     *   it's valid.
     */
    localActiveCampaign() {
      if (
        this.localActiveCampaign &&
        this.localActiveCampaign === this.activeCampaign
      ) {
        return;
      }

      this.setCampaign({ campaign: this.localActiveCampaign, type: 'active' });

      cnvs.EventBus.emit('discount-campaign', {
        active: this.localActiveCampaign,
        promoted: this.localPromotedCampaign,
      });
    },

    /**
     * Watch local promoted campaign to update Pinia state.
     */
    localPromotedCampaign: {
      handler() {
        this.setCampaign({
          campaign: this.localPromotedCampaign,
          type: 'promoted',
        });

        cnvs.EventBus.emit('discount-campaign', {
          active: this.localActiveCampaign,
          promoted: this.localPromotedCampaign,
        });
      },
      immediate: true,
    },

    async hydrated() {
      if (this.hydrated) {
        try {
          await this.resolveCampaignsOnHydration();
        } finally {
          // Always flip this, even on error, so badges relying on it don't
          // stay stuck on their skeleton placeholder forever.
          this.setCampaignsReady(true);
        }
      }
    },
  },

  mounted() {
    // Instagram (and other social) in-app browsers restore the page without
    // re-navigating, so the Shopify cookie and URL param are gone on second click.
    // We persist the code to sessionStorage and re-apply on visibility restore,
    // but only inside known in-app browsers to avoid interfering with normal tab switching.
    const isInAppBrowser =
      /Instagram|FBAN|FBAV|Twitter|BytedanceWebview|musical_ly/i.test(
        navigator.userAgent,
      );
    if (isInAppBrowser) {
      this.onVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        const savedCode = sessionStorage.getItem('esn_url_discount');
        if (!savedCode) return;
        const isApplied = (this.cart.discount_codes || []).some(
          (applyCode) => applyCode.code.toUpperCase() === savedCode,
        );
        if (!isApplied) {
          this.setInitialCode(savedCode);
          this.setCodeState('ready');
          this.setInitialCheck(true);
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  },

  beforeUnmount() {
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  },

  methods: {
    async resolveCampaignsOnHydration() {
      await this.$nextTick();

      /**
       * Update local state.
       */
      this.campaigns = this.$variable('discount.campaigns');
      this.influencers = this.$variable('discount.influencers');
      this.privateCampaigns = this.$variable('discount.privateCampaigns');
      this.campaignCodes = this.$variable('discount.campaignCodes').map(
        (code) => code.toUpperCase(),
      );

      const cartDiscounts = this.cart.discount_codes.filter(
        (code) => code.applicable === true,
      );

      // check if discount_codes is not empty
      const hasDiscountAttribute = Boolean(
        this.cart?.attributes?.discount_codes &&
        this.cart.attributes.discount_codes.trim() !== '',
      );

      // if it does not exist or is empty, deactivate campaign
      if (!hasDiscountAttribute) {
        this.setCampaign({ campaign: false, type: 'active' });
        this.setCodeState('ready');
      }
      // always set enteredCode as empty, as a default
      this.setEnteredCode('');

      if (cartDiscounts.length > 0) {
        for (const discount of cartDiscounts) {
          this.setInitialCheck(true);
          this.setEnteredCode(discount.code.toUpperCase());
        }
      } else if (hasDiscountAttribute) {
        /**
         * Influencer/private/generic-campaign codes are never redeemed as a
         * real Shopify discount code, so they never show up in
         * `cart.discount_codes` (the loop above). They only exist as the
         * `discount_codes` cart attribute (`code:type`), which is
         * server-side/cart-scoped and survives regardless of whether the
         * Pinia store's own localStorage persistence is available. Without
         * this, `active` would stay empty on every fresh page load unless
         * persistence happens to restore it - re-derive the code from the
         * attribute so it always gets re-resolved via determineValidCode().
         */
        const attributeCode = this.cart.attributes.discount_codes.split(':')[0];

        if (attributeCode) {
          this.setInitialCheck(true);
          this.setEnteredCode(attributeCode.toUpperCase());
        }
      }
      // Isolate this step - a failure here (e.g. a malformed cookie/URL
      // read) shouldn't prevent the code-resolution and campaign-validation
      // steps below from still running.
      try {
        await this.setDiscountFromUrl();
      } catch (error) {
        cnvs.ReportError('Failed to set discount from URL', error);
      }

      /**
       * The `setEnteredCode()` calls above (cart discounts loop and/or
       * `setDiscountFromUrl()`) trigger the `enteredCode` watcher, which
       * kicks off the async `determineValidCode()` chain that resolves and
       * activates the *real* code (e.g. a code newly applied via URL).
       * That chain is not awaited by the watcher itself, so wait here for
       * it to fully settle before checking `this.activeCampaign` below -
       * otherwise `checkCampaignCode()` would re-validate the stale,
       * localStorage-persisted `activeCampaign` (e.g. an old campaign code)
       * while the new code is still resolving, and its faster completion
       * would win the race and re-assert the stale campaign as active.
       */
      await this.$nextTick();
      if (this.pendingCodeResolution) {
        await this.pendingCodeResolution;
        this.pendingCodeResolution = null;
      }

      /**
       * Verify code and sync campaign data from metafield.
       * This checks for campaign removal, updates discount groups, and
       * validates dates/markets. `this.activeCampaign` at this point could
       * have been read from localStorage Pinia cache.
       * - Isolated in its own try/catch so a failure here doesn't abort the
       *   rest of the hydration chain (e.g. the custom cookie check below)
       *   and leave `active` in an unverified state that badges would then
       *   trust as final once `campaignsReady` flips.
       */
      if (this.activeCampaign) {
        try {
          await this.checkCampaignCode();
        } catch (error) {
          cnvs.ReportError('Failed to verify active campaign', error);
          // Don't let badges/prices trust a campaign we couldn't verify.
          this.setCampaign({ campaign: false, type: 'active' });
        }
      }

      /**
       * Set discount code from custom cookie.
       * - If there is a discount code in the URL then we do not want to take
       *   it from the cart or cookie as it is already set in cart right away
       */
      if (!this.urlDiscount) {
        this.setDiscountCodeFromCustomCookie();
      }
    },

    ...mapActions(useDiscountCampaignsStore, {
      setCampaign: 'setCampaign',
      setEnteredCode: 'setEnteredCode',
      setRemoveCode: 'setRemoveCode',
      setInitialCode: 'setInitialCode',
      setCodeState: 'setCodeState',
      setUrlDiscount: 'setUrlDiscount',
      setErrorType: 'setErrorType',
      setCartItemPrices: 'setCartItemPrices',
      setValidNativeCode: 'setValidNativeCode',
      setInitialCheck: 'setInitialCheck',
      setFreeShipping: 'setFreeShipping',
      setCampaignsReady: 'setCampaignsReady',
      refreshCampaignThresholds: 'refreshCampaignThresholds',
      loadFreeProducts: 'loadFreeProducts',
    }),
    ...mapActions(useCartStore, {
      updateCartAttributes: 'updateCartAttributes',
      setRemoveOrderCode: 'setRemoveOrderCode',
      updateFreebies: 'updateFreebies',
      booksInCart: 'booksInCart',
      depositInCart: 'depositInCart',
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    /**
     * Set discount code from URL.
     * - Shopify sets a cookie whenever code comes via url
     * - we clear the cookie to avoid issues in checkout
     */
    setDiscountFromUrl() {
      const urlCookie = Cookies.get('discount_code');
      const urlParamCode = new URLSearchParams(window.location.search).get(
        'discount',
      );
      const rawCode = urlCookie || urlParamCode;

      if (!rawCode) {
        this.setUrlDiscount(false);
        return;
      }
      const localCode = rawCode.toUpperCase();

      const discountCodes = this.cart.discount_codes || [];
      const isCodeAlreadyApplied = discountCodes.some(
        (code) => code.code.toUpperCase() === localCode,
      );

      const activeCampaignCode =
        this.cart.attributes?.discount_codes?.split(':')[0] || '';

      if (
        isCodeAlreadyApplied &&
        activeCampaignCode.toUpperCase() === localCode
      ) {
        Cookies.remove('discount_code');
        return;
      }

      this.setInitialCode(localCode);
      Cookies.remove('discount_code');
      sessionStorage.setItem('esn_url_discount', localCode);
      this.setCodeState('ready');
      this.setInitialCheck(true);
    },

    async checkCampaignCode() {
      // First sync campaign data from metafield before validation
      let metafieldActiveCampaign = this.campaigns.find(
        (campaign) => campaign.campaignId === this.activeCampaign.campaignId,
      );
      // all private codes are uppercase, so check against that
      if (
        this.activeCampaign.code.some((code) => {
          return validPrivateCodes().includes(code.toUpperCase());
        })
      ) {
        metafieldActiveCampaign = this.privateCampaigns.find(
          (campaign) => campaign.campaignId === this.activeCampaign.campaignId,
        );
      }

      const campaignCode = getCampaignCode();

      if (!metafieldActiveCampaign) {
        // Campaign was removed from metafield, clear it
        if (campaignCode) {
          await this.clearCode('invalid', campaignCode, 'expired');
        }
        return false;
      }

      // Sync any changes from metafield to active campaign
      const updatedCampaign = this.checkCampaignUpdate(metafieldActiveCampaign);
      // Use the updated campaign if sync happened, otherwise use current
      const campaignToValidate = updatedCampaign || this.activeCampaign;
      this.localActiveCampaign = campaignToValidate;

      const validDate = this.getValidCampaignDate(campaignToValidate);
      const validMarket = this.getValidCampaignMarket(campaignToValidate);

      if (!validDate || !validMarket) {
        const errorType = validDate ? 'market' : 'expired';

        this.setCodeState('invalid');

        if (campaignCode) {
          this.setInitialCheck(true);
          await this.clearCode('invalid', campaignCode, errorType);
        }
        this.setErrorType(errorType);
        return false;
      }

      return true;
    },
    /**
     * Sync campaign data from metafield to active campaign.
     * Updates all campaign properties including dates, discounts, freebies, shipping, etc.
     * @returns {Object|null} Updated campaign object if changes detected, null otherwise
     */
    checkCampaignUpdate(metafieldActiveCampaign) {
      // eslint-disable-next-line no-unused-vars
      const { code, maxDiscount, ...activeCampaignData } = this.activeCampaign;
      const metafieldForComparison = JSON.stringify(metafieldActiveCampaign);
      const activeCampaignForComparison = JSON.stringify(activeCampaignData);

      const campaignIsDifferent =
        metafieldForComparison !== activeCampaignForComparison;

      if (campaignIsDifferent) {
        // Calculate new maxDiscount from discounts array
        const discountsArray = metafieldActiveCampaign.discounts || [];
        const newMaxDiscount =
          discountsArray.length > 0
            ? Math.max(...discountsArray.map((discount) => discount.discount))
            : 0;

        let codeArray = this.activeCampaign.code;

        if (metafieldActiveCampaign.code) {
          codeArray = Array.isArray(metafieldActiveCampaign.code)
            ? metafieldActiveCampaign.code
            : [metafieldActiveCampaign.code];
        }

        const updatedCampaign = {
          ...metafieldActiveCampaign,
          code: codeArray,
          maxDiscount: newMaxDiscount,
        };

        this.setCampaign({ campaign: updatedCampaign, type: 'active' });
        return updatedCampaign;
      }

      return null;
    },

    /**
     * Check if code is native code.
     * @returns {boolean}
     */
    isNativeCode(code) {
      return (
        this.validNativeCode === code &&
        this.validNativeCode !== '' &&
        !validCampaignCodes().includes(code) &&
        !validInfluencerCodes().includes(code) &&
        !validPrivateCodes().includes(code)
      );
    },

    /**
     * Set discount code based on custom cookies.
     * - Applies discount code when already applied to another sub-domain.
     */
    setDiscountCodeFromCustomCookie() {
      const changedBy = Cookies.get(
        this.$variable('settings.discounts.changedByCookie'),
      );
      const changeTo = Cookies.get(
        this.$variable('settings.discounts.changeToCookie'),
      )?.toUpperCase();

      if (
        !changedBy ||
        !changeTo ||
        changedBy === this.locationHost ||
        changeTo === getCampaignCode()
      ) {
        return;
      }

      this.setInitialCheck(true);
      this.setInitialCode(changeTo.toUpperCase());
    },

    /**
     * Clear discount code, active campaign, and cookies.
     * @param {String} state - Code state to set.
     * @param {String} code - Code to clear.
     * @param {String} [errorType] - If state is `invalid` then provide error.
     */
    async clearCode(state, code, errorType) {
      try {
        if (state) {
          this.setCodeState(state);
        }

        if (errorType) {
          this.setErrorType(errorType);
        }

        // only deactivateActive Campaign if campaign code is thrown out
        if (getCampaignCode() === code.toUpperCase()) {
          this.localActiveCampaign = false;
          this.clearCookies();
        }

        await this.removeCartAttribute(code);
        this.refreshCampaignThresholds();
        await this.updateFreebies();

        const hasShippingCode = this.cart.discount_codes.filter(
          (discount) => getTypes(discount.code) === 'shipping',
        );

        if (getTypes(code) === 'shipping' && !hasShippingCode.length) {
          this.setFreeShipping(false);
        }

        this.setCartItemPrices([{}]);

        this.setRemoveCode('');
        this.localCode = '';
      } catch (error) {
        cnvs.ReportError('Failed to clear code', error);
      }
    },

    /**
     * Determine if the code is valid and set campaigns if valid one exists.
     */
    async determineValidCode() {
      this.setCodeState('loading');
      this.localCode = this.enteredCode;
      const code = this.localCode;
      /**
       * Capture the intent locally instead of re-reading `this.urlDiscount`
       * (global Pinia state) at each step below. `this.urlDiscount` can be
       * mutated by a *different*, concurrently-resolving `setEnteredCode()`
       * call (e.g. the campaign-teaser web component's redeem button, which
       * calls it directly on this store, bypassing this method entirely) -
       * re-reading it after an `await` risks reading a value that belongs to
       * that other resolution, not this one.
       */
      const isUrlDiscount = this.urlDiscount;

      try {
        if (await this.handleCampaign(code, isUrlDiscount)) {
          return;
        }

        if (await this.handleInfluencer(code, isUrlDiscount)) {
          return;
        }

        if (await this.handlePrivate(code, isUrlDiscount)) {
          return;
        }

        if (await this.handleNative(code, isUrlDiscount)) {
          return;
        }

        this.clearCode('invalid', code, 'invalid');
        if (isUrlDiscount) {
          this.setUrlDiscount(false);
        }
      } catch (error) {
        this.clearCode('error', code, error.message);
      }
    },

    async handleCampaign(code, isUrlDiscount) {
      const isCampaignCode =
        validCampaignCodes().includes(code) ||
        this.campaignCodes.includes(code);
      if (!isCampaignCode) return false;

      const campaign = this.getValidDiscountCampaign();
      if (!campaign) return false;

      if (campaign.error) {
        this.clearCode('invalid', code, campaign.error);
      } else {
        await this.setActiveCampaign(campaign, isUrlDiscount, code);
      }

      return true;
    },

    async handleInfluencer(code, isUrlDiscount) {
      if (!validInfluencerCodes().includes(code)) return false;

      const campaign = this.getValidInfluencerCampaign();
      if (!campaign) return false;

      await this.setActiveCampaign(campaign, isUrlDiscount, code);
      return true;
    },

    async handlePrivate(code, isUrlDiscount) {
      if (!validPrivateCodes().includes(code)) return false;

      const campaign = this.getValidPrivateCampaign();
      if (!campaign || campaign.error) return false;

      await this.setActiveCampaign(campaign, isUrlDiscount, code);
      return true;
    },

    async handleNative(code, isUrlDiscount) {
      const isApplicable = await this.checkCartIfApplicable(code);
      const codeUpperCase = code.toUpperCase();
      const isOrderDiscount = isOrderCode(codeUpperCase);

      /** special case: non applicable discounts + discount url - show popup */
      if (
        isUrlDiscount &&
        !isApplicable &&
        getCampaignCode() !== codeUpperCase
      ) {
        this.urlApplicable = false;
        this.checkNotificationPopup(isUrlDiscount, codeUpperCase);
      }

      if (!isApplicable) {
        this.setValidNativeCode('');
        return false;
      }

      this.urlApplicable = true;
      this.setValidNativeCode(codeUpperCase);

      if (!this.isNativeCode(codeUpperCase)) {
        return false;
      }

      // special case: empty cart with a price of 0 means an order discount is invalid
      if (
        this.cart.total_price === 0 &&
        this.cart.items.length > 0 &&
        isOrderDiscount
      ) {
        this.clearCode('invalid', code, 'order');
        this.setEnteredCode('');
        return true;
      }

      // special case: books or deposits in cart means an order discount is invalid
      if (isOrderDiscount && this.booksInCart().length) {
        this.clearCode('invalid', codeUpperCase, 'book');
        this.setEnteredCode('');
        return true;
      }

      if (isOrderDiscount && this.depositInCart().length) {
        this.clearCode('invalid', codeUpperCase, 'deposit');
        this.setEnteredCode('');
        return true;
      }

      this.setCodeState('valid');
      await this.setCartAttributes(code);
      if (!this.checkApplicability(code)) {
        return true;
      }
      await this.removeInactiveCodes();

      if (this.initialCheck) {
        this.setCodeState('ready');
        this.setInitialCheck(false);
      }

      if (isUrlDiscount) {
        this.checkNotificationPopup(isUrlDiscount, codeUpperCase);
      }
      this.checkFreeShipping();
      this.refreshCampaignThresholds();
      await this.updateFreebies();

      return true;
    },

    /**
     * Check if there is a shipping code in the cart.
     * - If there is a shipping code then set free shipping to true.
     * - If there is no shipping code then set free shipping to false.
     */
    checkFreeShipping() {
      const discountCodes = this.cart.discount_codes || [];

      const hasShippingCode = discountCodes.some((code) => {
        return getTypes(code.code) === 'shipping' && code.applicable;
      });

      if (hasShippingCode) {
        this.setFreeShipping(true);
        return;
      }
      this.setFreeShipping(false);
    },

    /**
     * Remove inactive discount codes from cart.
     * - exception for campaign codes when there are no items in cart
     */
    async removeInactiveCodes() {
      const discountCodes = this.cart.discount_codes || [];
      const inactiveCodes =
        this.cart.items.length > 0
          ? discountCodes.filter((code) => !code.applicable)
          : discountCodes.filter(
              (code) =>
                !code?.applicable &&
                getCampaignCode() !== code.code.toUpperCase(),
            );

      if (inactiveCodes.length) {
        await Promise.all(
          inactiveCodes.map((code) => this.clearCode('invalid', code.code)),
        );
      }
    },

    /**
     * If discount is set from URL then open success modal.
     * Also prevent campaign popup from stacking with the success overlay.
     * - if codes are non-applicable we show a special message
     */
    checkNotificationPopup(isUrlDiscount, code) {
      if (
        !isUrlDiscount ||
        this.$variable('request.pageType') === 'cart' ||
        this.activeOverlay.find((overlay) => overlay.namespace === 'cartDrawer')
      ) {
        return;
      }

      // Set before reading `notificationCode` below so the computed (which
      // falls back to `newNotificationCode`) reflects the code this specific
      // resolution is for, not whatever `enteredCode`/`localCode` happen to
      // hold by now - those are shared fields another, concurrently-resolving
      // code entry may have already overwritten.
      this.newNotificationCode = code;

      const codeType = getTypes(code);
      this.notificationText = this.$string(
        'discount_campaigns.activated_modal.content_html',
      );

      /**
       * Shipping needs to be changed according to shipping database
       */
      if (codeType === 'shipping') {
        this.notificationText = this.$string(
          'discount_campaigns.activated_modal.shipping_content_html',
        );
      }

      if (codeType === 'order') {
        const orderDiscount = this.cart.cart_level_discount_applications.find(
          (app) =>
            app.title.toUpperCase() === code.toUpperCase() &&
            app.target_selection === 'all' &&
            app.value !== '0.0' &&
            app.total_allocated_amount !== 0,
        );

        const orderValue = orderDiscount
          ? this.$formatMoney(parseInt(orderDiscount.value) * 100)
          : 0;

        this.notificationText = this.$string(
          'discount_campaigns.activated_modal.order_content_html',
          {
            replace: {
              value: orderValue,
            },
          },
        );
      }

      if (!this.urlApplicable) {
        this.notificationText = this.$string(
          'discount_campaigns.activated_modal.non_applicable_html',
        );
      }

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'discountCampaignsActivatedModal',
        clearQueue: true,
      });

      this.setUrlDiscount(false);
      this.setCodeState('ready');
    },

    /**
     * Get and set active campaign.
     * - Find campaigns with matching code and market.
     * - There must be a matching code because determineValidCampaign() is only
     *   called when the code is valid.
     * @returns {Object}
     */
    getValidDiscountCampaign() {
      let matches = this.campaigns?.filter((campaign) => {
        return (
          campaign.type === 'discount' &&
          campaign.code.includes(this.enteredCode)
        );
      });

      // `campaignCodes` is a shared/generic code, not tied to one specific
      // campaign - `matches` becomes every discount campaign across every
      // market, so a market-filter wipeout here is normal multi-market
      // filtering, not a signal that any particular campaign is
      // misconfigured. Only the code-specific branch above identifies a
      // single campaign meant for this code, so only that one is reportable.
      const isGenericCode = this.campaignCodes.includes(this.enteredCode);

      if (isGenericCode) {
        matches = this.campaigns?.filter((campaign) => {
          return campaign.type === 'discount';
        });
      }
      /**
       * If no matches there must be an internal error.
       */
      if (!matches.length) {
        return { error: 'internal' };
      }

      /**
       * Filter valid markets.
       */
      const codeMatches = matches;
      matches = matches.filter(this.getValidCampaignMarket);

      if (!matches.length) {
        if (!isGenericCode) {
          this.reportCampaignMarketMismatch(codeMatches);
        }
        return { error: 'market' };
      }

      /**
       * Filter valid dates.
       */
      matches = matches.filter(this.getValidCampaignDate);

      if (!matches.length) {
        return { error: 'expired' };
      }

      /**
       * Return first valid campaign.
       */
      return matches[0];
    },

    /**
     * Determine details and set active campaign.
     * @param {Object} campaign - Campaign to make active.
     * @param {Boolean} isUrlDiscount - Whether this resolution was triggered
     * by a URL-applied code, captured by the caller at the start of its own
     * resolution attempt (not read live here - see `determineValidCode()`).
     * @param {String} code - The code being resolved, captured by the caller
     * for the same reason.
     */
    async setActiveCampaign(campaign, isUrlDiscount, code) {
      /**
       * Determine best discount in campaign.
       */
      const discounts = campaign.discounts
        .map((entry) => entry.discount)
        .sort((a, b) => a - b)
        .reverse();

      /**
       * Set active campaign.
       * - Add code as this is missing from influencer type campaigns.
       */

      let codeArray = [code];

      if (campaign.code) {
        codeArray = Array.isArray(campaign.code)
          ? campaign.code
          : [campaign.code];
      }

      /**
       * Codes matched via the generic `campaignCodes` list (see
       * getValidDiscountCampaign()) don't appear in the campaign's own
       * canonical `code` array, but that's the code actually applied to the
       * cart in setCartAttributes() below. Keep it in `code` too so
       * isCampaignAppliedInCart() can confirm the campaign against the cart.
       */
      if (
        code &&
        !codeArray.some((entry) => entry.toUpperCase() === code.toUpperCase())
      ) {
        codeArray = [...codeArray, code];
      }

      this.localActiveCampaign = {
        ...campaign,
        code: codeArray,
        maxDiscount: discounts[0],
      };

      /**
       * Set attributes and statuses.
       */

      if (isUrlDiscount) {
        this.newNotificationCode = code;
      }

      await this.setCartAttributes(code);
      if (!this.checkApplicability(code)) {
        return;
      }

      const isCampaignApplicable = this.cart.discount_codes.some(
        (discount) =>
          discount.code.toUpperCase() === code.toUpperCase() &&
          discount.applicable === true,
      );

      /**
       * The code is already committed to the cart at this point (confirmed
       * by `checkApplicability()` above). Everything below is bookkeeping
       * (thresholds, free products, freebies, cross-domain cookies) - if it
       * were awaited un-guarded, a failure here would bubble up to
       * `determineValidCode()`'s catch, which calls `clearCode('error', ...)`
       * and strips the just-applied code back out of the cart. Contain
       * failures here instead so a secondary step failing can't roll back
       * a primary action that already succeeded.
       */
      try {
        if (isUrlDiscount) {
          this.urlApplicable =
            this.cart.items.length === 0 ? true : isCampaignApplicable;
          this.checkNotificationPopup(isUrlDiscount, code);
        }
        await this.removeInactiveCodes();
        this.refreshCampaignThresholds();
        await this.loadFreeProducts();

        await this.updateFreebies();

        this.setCodeState('valid');

        if (this.initialCheck) {
          this.setCodeState('ready');
          this.setInitialCheck(false);
        }
        this.setCookies();
      } catch (error) {
        cnvs.ReportError('Failed to finalise applied campaign', error);
      }
    },

    /**
     * Get valid campaign date.
     * - Determine if market has valid dates.
     * @param {Object} campaign - Campaign to test.
     * @returns {Boolean}
     */
    getValidCampaignDate(campaign) {
      if (!campaign) {
        return false;
      }

      return dateTime(campaign?.startDate, campaign?.endDate);
    },

    /**
     * Get valid campaign market.
     * - Determine if current market is valid for active campaign.
     * - Pure predicate, no side effects: this is used as a bare
     *   filter/find callback against `this.campaigns`, which holds every
     *   market's campaigns unfiltered (see wiki performance trap), so most
     *   calls are *expected* to reject a campaign for being some other
     *   market's - that's normal filtering, not a misconfiguration. Report
     *   only at the call sites that resolve a specific/single campaign.
     * @param {Object} campaign - Campaign to test.
     * @returns {Boolean}
     */
    getValidCampaignMarket(campaign) {
      if (!campaign) {
        return false;
      }

      return campaign?.markets?.length
        ? campaign.markets.some((market) => isCurrentMarket(market))
        : true;
    },

    /**
     * Report a campaign/private-code match that has markets configured,
     * valid dates, but no match for the current market - a misconfiguration
     * (e.g. a market added to Shopify but never added to the campaign)
     * that would otherwise fail silently. See wiki domain doc "Market
     * matching" for why this only reports here rather than from inside
     * `getValidCampaignMarket` itself.
     * `cnvs.ReportError` always throws after reporting (see
     * src/scripts/core/global-functions.js), so the call is wrapped -
     * otherwise a reporting failure would abort whichever code-resolution
     * flow called this.
     * @param {Array<Object>} candidates - Campaigns matched by code, before
     * market filtering.
     */
    reportCampaignMarketMismatch(candidates) {
      const misconfigured = candidates?.find((campaign) => {
        return campaign?.markets?.length && this.getValidCampaignDate(campaign);
      });

      if (!misconfigured) {
        return;
      }

      try {
        cnvs.ReportError(
          'Campaign markets configured but no match for current market',
          new Error(`Campaign ${misconfigured.campaignId} matched no market`),
        );
      } catch {
        // Already reported to Bugsnag/console inside cnvs.ReportError - see
        // comment above for why the resulting throw must not propagate.
      }
    },

    /**
     * Get valid influencer campaign.
     * @returns {Object|Boolean}
     */
    getValidInfluencerCampaign() {
      return this.campaigns?.find((campaign) => {
        return (
          campaign.type === 'influencer' &&
          this.getValidCampaignDate(campaign) &&
          this.getValidCampaignMarket(campaign)
        );
      });
    },

    /**
     * Get valid private campaign.
     * @returns {Object|Boolean}
     */
    getValidPrivateCampaign() {
      let matches = this.privateCampaigns?.filter((campaign) => {
        return campaign.code.toUpperCase() === this.enteredCode;
      });

      /**
       * If no matches there must be an internal error.
       */
      if (!matches.length) {
        return { error: 'internal' };
      }

      /**
       * Filter valid markets.
       */
      const codeMatches = matches;
      matches = matches.filter(this.getValidCampaignMarket);

      if (!matches.length) {
        this.reportCampaignMarketMismatch(codeMatches);
        return { error: 'market' };
      }

      /**
       * Filter valid dates.
       */
      matches = matches.filter(this.getValidCampaignDate);

      if (!matches.length) {
        return { error: 'expired' };
      }

      /**
       * Return first valid campaign.
       */
      return matches[0];
    },

    async removeCartAttribute(removeCode) {
      try {
        // array of codes in cart
        const discountArray = this.cart.discount_codes.map((disc) =>
          disc.code.toLowerCase(),
        );

        let discountCodesAttr = this.cart.attributes?.discount_codes || '';
        const currentCartAttribute = this.getCartAttributeCode();

        const removeCodeIsCampaignCode = Boolean(
          removeCode.toLowerCase() === currentCartAttribute &&
          currentCartAttribute !== '',
        );

        if (removeCodeIsCampaignCode) {
          discountCodesAttr = null;
        }

        const updateDiscount = discountArray.filter(
          (entry) => entry?.toLowerCase() !== removeCode.toLowerCase(),
        );

        const discount = updateDiscount ? updateDiscount.join(',').trim() : '';

        const payload = {
          attributes: { discount_codes: discountCodesAttr },
          discount,
        };
        const response = await this.updateCartAttributes(payload);
        return response;
      } catch (error) {
        cnvs.ReportError('Failed to remove cart attribute', error);
        throw error;
      }
    },

    async checkCartIfApplicable(code) {
      try {
        const enteredCode = code?.toLowerCase();
        const discountArray = this.cart.discount_codes.map((disc) =>
          disc.code.toLowerCase(),
        );

        // do we need logic here to throw out a product off code?

        discountArray.push(enteredCode);

        const payload = {
          discount: discountArray.join(',').trim(),
        };

        // Transient network failures here (e.g. flaky connection) would
        // otherwise abort the whole hydration chain and leave badges reading
        // a stale/unverified campaign, so retry before giving up.
        const response = await retryWithBackoff(() =>
          this.updateCartAttributes(payload),
        );

        // If no codes returned, treat as not applicable
        if (!response?.discount_codes?.length) {
          return false;
        }

        const isApplicable = response.discount_codes.find(
          (disc) => disc.code === enteredCode,
        )?.applicable;

        // Same guard as `checkApplicability()`: don't clear `enteredCode` if
        // a different, concurrently-resolving code entry has already moved
        // it on.
        if (!isApplicable && this.enteredCode?.toLowerCase() === enteredCode) {
          this.setEnteredCode('');
        }

        return Boolean(isApplicable);
      } catch (error) {
        cnvs.ReportError('Failed to set cart attributes', error);
        throw error;
      }
    },

    /**
     * Set cart attributes and discount.
     * - Currently there is no validation so use custom validation.
     * @param {String} code - Code to apply. Falls back to the live
     * `enteredCode` for the rare caller that doesn't have its own resolved
     * code in scope, but callers resolving a specific code should always
     * pass it explicitly - `enteredCode` is shared, mutable Pinia state that
     * a concurrently-resolving code entry can overwrite mid-flight.
     * @returns {Promise}
     */
    setCartAttributes(code = this.enteredCode) {
      /* eslint-disable camelcase */
      return new Promise(async (resolve, reject) => {
        try {
          let discount_codes = this.cart.attributes?.discount_codes || '';
          const codeType = getTypes(code);
          const cart_token = this.cart.token;
          const currentCartAttribute = discount_codes?.split(':')[0] || '';
          let discountArray = this.cart.discount_codes.map((disc) =>
            disc.code.toLowerCase(),
          );

          if (['order', 'shipping'].includes(codeType)) {
            discountArray = this.replaceOrderShippingCode(
              discountArray,
              currentCartAttribute,
            );
          }

          if (!discountArray.includes(code?.toLowerCase())) {
            discountArray.push(code?.toLowerCase());
          }

          // if we have a code type, we need to set the discount codes attribute
          // as it must be infl/priv/pub
          if (['infl', 'priv', 'pub', 'product'].includes(codeType)) {
            discount_codes = `${code?.toLowerCase()}:${codeType}`;

            discountArray = this.replaceCampaignOrProductCode(
              discountArray,
              currentCartAttribute,
              code,
            );
          }

          const discount = discountArray.join(',').trim();

          const payload = {
            attributes: { discount_codes, cart_token },
            discount,
          };

          const response = await this.updateCartAttributes(payload);

          resolve(response);
        } catch (error) {
          cnvs.ReportError('Failed to set cart attributes', error);
          reject(error);
        }
      });
      /* eslint-enable camelcase */
    },

    replaceCampaignOrProductCode(discountArray, currentCartAttribute, code) {
      let localArray = discountArray;
      // check if campaign code or product already part of the discounts, if so remove it
      if (code?.toLowerCase() !== currentCartAttribute) {
        const removeCurrentCartAttribute = discountArray.filter(
          (discount) => discount !== currentCartAttribute,
        );
        localArray = removeCurrentCartAttribute;
      }

      return localArray;
    },

    replaceOrderShippingCode(discountArray, currentCartAttribute) {
      const hasOtherCode =
        discountArray.filter((discount) => discount !== currentCartAttribute)
          .length > 0;
      let array = discountArray;

      if (hasOtherCode) {
        const removeOtherCode = discountArray.filter(
          (discount) => discount === currentCartAttribute,
        );
        array = removeOtherCode;
      }

      return array;
    },

    /**
     * After code has been applied, it can have applicable: false due
     * to invalid combination with other codes.
     * - Check if code is applicable and if not, clear it.
     */
    checkApplicability(code = this.enteredCode) {
      const discountCodes = this.cart.discount_codes || [];
      const isApplicable = discountCodes.find(
        (discount) => discount.code.toLowerCase() === code.toLowerCase(),
      )?.applicable;

      if (isApplicable === false && this.cart.items.length > 0) {
        this.clearCode('invalid', code, 'combine');
        return false;
      }

      /**
       * Only clear the shared `enteredCode` if it still holds the code we
       * just checked - a concurrently-resolving code entry may have already
       * moved it on to a different code, which this call must not clobber.
       */
      if (this.enteredCode?.toLowerCase() === code?.toLowerCase()) {
        this.setEnteredCode('');
      }
      return true;
    },

    /**
     * Set custom cookies.
     * - Create custom cookies used by other ESN stores to set the active
     *   discount code when switching sub-domains.
     * - Cookie names and values come from 1.0 codebase.
     * @returns {Promise}
     */
    setCookies() {
      return new Promise(async (resolve, reject) => {
        try {
          const config = this.getCookieConfig();
          await Cookies.set(
            this.$variable('settings.discounts.changedByCookie'),
            this.locationHost,
            config,
          );
          await Cookies.set(
            this.$variable('settings.discounts.changeToCookie'),
            getCampaignCode(),
            config,
          );
          resolve();
        } catch (error) {
          cnvs.ReportError('Failed to set custom cookie', error);
          reject(error);
        }
      });
    },

    /**
     * Get cookie config.
     * @returns {Object}
     */
    getCookieConfig() {
      return {
        domain: this.$variable('settings.discounts.discountCookieDomain'),
      };
    },

    /**
     * Clear custom cookies.
     * - 1.0 stores don't clear active discount code from other stores when
     *   clearing discount on a store.
     * @returns {Promise}
     */
    clearCookies() {
      return new Promise(async (resolve, reject) => {
        try {
          const config = this.getCookieConfig();
          await Cookies.remove(
            this.$variable('settings.discounts.changedByCookie'),
            config,
          );
          await Cookies.remove(
            this.$variable('settings.discounts.changeToCookie'),
            config,
          );
          resolve();
        } catch (error) {
          cnvs.ReportError('Failed to clear cookies', error);
          reject(error);
        }
      });
    },
  },
};
</script>
